import { Box, Container, Grid, Typography } from "@mui/material"
import { ColumnDef, Row } from "@tanstack/react-table"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import ClpTable from "components/ClpTable"
import { MultiSelectFilter } from "components/ClpTable/ClpTableFilterTypes"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import { TableLoadingStateEnum } from "components/ClpTable/tableProps.interface"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { ChipInfoStatusEnum } from "components/Status/ChipInfo/chipInfo.interface"
import ChipInfoIcon from "components/Status/ChipInfo/ChipInfoIcon"

import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoutes } from "generated/routes"
import moment from "moment"
import { useContext, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { StyledTableContainer } from "shared/styled/Global"
import { includesPrimitiveFilter } from "components/ClpTable/components/helpers/includesFilterFn"

const ViolationList = () => {
  const history = useHistory()
  const loadingContext = useContext(GlobalLoaderContext)
  const [violationResult, violationApi] = useApi() as [IApiResult, IApiFunc]
  const { t } = useTranslation()

  useEffect(() => {
    violationApi.get(apiEndpoints.violation.get)
    return () => {
      violationApi.leave()
    }
  }, [])

  useEffect(() => {
    loadingContext.setLoading(() => violationResult.status === 1)
  })

  const status = (value: any) => {
    switch (value) {
      case 1:
        return <ChipInfoIcon type={ChipInfoStatusEnum.Booked} />
      case 2:
        return <ChipInfoIcon type={ChipInfoStatusEnum.Completed} />
      default:
        return <ChipInfoIcon type={ChipInfoStatusEnum.Active} />
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "name",
      header: t(`violation.violation_driver`, ``),
      cell: ({ cell }: any) => {
        return cell.row.original.name ?? t("violation.missing_linked_vehicle_card", "(Missing linked vehicle card)")
      }
    },
    {
      accessorKey: "violationDate",
      header: t(`violation.violation_date`, ``),
      cell: ({ cell }: any) => {
        return cell.row.original.violationDate
          ? moment.utc(cell.row.original.violationDate).local().format("YYYY-MM-DD HH:mm")
          : "-"
      }
    },
    {
      accessorKey: "createdDate",
      header: t(`violation.created_in_tp`, ``),
      cell: ({ cell }: any) => {
        return cell.row.original.createdDate
          ? moment.utc(cell.row.original.createdDate).local().format("YYYY-MM-DD HH:mm")
          : "-"
      }
    },
    {
      accessorKey: "updatedByDriver",
      header: t(`violation.updated_by_driver`, ``),
      cell: ({ cell }: any) => {
        return cell.row.original.updatedByDriver
          ? moment.utc(cell.row.original.updatedByDriver).local().format("YYYY-MM-DD HH:mm")
          : "-"
      }
    },
    {
      accessorKey: "updatedByName",
      header: t(`violation.updated_by_name`, ``),
      cell: ({ cell }: any) => {
        return cell.row.original.updatedBy
      }
    },
    {
      accessorKey: "updatedDate",
      header: t(`violation.updated`, ``),
      cell: ({ cell }: any) => {
        return cell.row.original.updatedDate
          ? moment.utc(cell.row.original.updatedDate).local().format("YYYY-MM-DD HH:mm")
          : "-"
      }
    },
    {
      accessorKey: "importType",
      header: t(`violation.import_type`, ``),
      cell: ({ cell }: any) => {
        return cell.row.original.importType
      }
    },
    {
      accessorFn: (row: any) => { return row.status },
      cell: ({ cell }: any) => {
        return status(cell.row.original.status)
      },
      header: t("global.status", "Status"),
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      meta: {
        filter: MultiSelectFilter,
        mainFilter: true,
        filterHeader: t("global.status", "Status"),
        filterOptions: [
          { name: t("global.active", "Active"), value: 0 },
          { name: t("global.ongoing", "Ongoing"), value: 1 },
          { name: t("global.completed", "Completed"), value: 2 }
        ],
      },
    },
    {
      accessorKey: "edit",
      header: "",
      meta: {
        align: "right"
      },
      cell: ({ cell }: any) => {
        return (
          <TableButton
            type={TableButtonTypeEnum.Edit}
            handleOnClick={() => history.push(allRoutes.ViolationDetails(cell.row.original.id))}
          />
        )
      }
    }
  ]

  const listRows = violationResult.status === 2 ? violationResult.data : []

  return (
    <div>
      <Container>
        <Box>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box order={2} flexGrow={2}>
              <Grid item>
                <Typography variant="h2">{t(`violation.violations`, ``)}</Typography>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Container>
      <StyledTableContainer>
        <ClpTable
          onRowClick={(row: Row<any>) => history.push(allRoutes.ViolationDetails(row.original.id))}
          columns={columns}
          rows={violationResult.status === 2 && listRows}
          loadingState={violationResult.status as TableLoadingStateEnum}
          tableId={ClpTableEnum.Violation}
        />
      </StyledTableContainer>
    </div>
  )
}

export default ViolationList
